/**
 * Determines the vehicle type based on comprehensive field analysis
 * @param {Object} vehicle - The vehicle object containing various properties
 * @returns {string} Vehicle type (sedan, suv, truck, van, coupe, hatchback, wagon, convertible)
 */
export const getVehicleType = (vehicle) => {
    if (!vehicle) return 'Sedan';

    // Extract all possible clues (handle both backend and frontend field names)
    const classValue = (vehicle.class || vehicle.body || '').toLowerCase().trim();
    const bodytype = (vehicle.bodytype || '').toLowerCase().trim();
    const model = (vehicle.model || '').toLowerCase().trim();
    const trimName = (vehicle.trimName || vehicle.trim_name || '').toLowerCase().trim();
    const realModel = (vehicle.realModel || '').toLowerCase().trim();

    // Combine all fields for comprehensive analysis
    const combined = `${classValue} ${bodytype} ${model} ${trimName} ${realModel}`.toLowerCase();

    // console.log('Vehicle type detection:', { vehicle, combined });

    // ========== TRUCK / PICKUP ==========
    if (
        combined.includes('truck') ||
        combined.includes('pickup') ||
        combined.includes('extended cab') ||
        combined.includes('crew cab') ||
        combined.includes('double cab') ||
        combined.includes('king cab') ||
        combined.includes('supercrew') ||
        combined.includes('supercab') ||
        // Specific truck models
        combined.includes('f-150') ||
        combined.includes('f-250') ||
        combined.includes('f-350') ||
        combined.includes('f-450') ||
        combined.includes('f150') ||
        combined.includes('f250') ||
        combined.includes('f350') ||
        combined.includes('silverado') ||
        combined.includes('sierra') ||
        combined.includes('ram 1500') ||
        combined.includes('ram 2500') ||
        combined.includes('ram 3500') ||
        combined.includes('tundra') ||
        combined.includes('tacoma') ||
        combined.includes('frontier') ||
        combined.includes('titan') ||
        combined.includes('ranger') ||
        combined.includes('colorado') ||
        combined.includes('canyon') ||
        combined.includes('gladiator') ||
        combined.includes('ridgeline') ||
        combined.includes('maverick') ||
        combined.includes('santa cruz')
    ) {
        return 'Truck';
    }

    // ========== VAN / MINIVAN ==========
    if (
        combined.includes('van') ||
        combined.includes('minivan') ||
        combined.includes('passenger van') ||
        combined.includes('cargo van') ||
        // Specific van models
        combined.includes('caravan') ||
        combined.includes('grand caravan') ||
        combined.includes('town & country') ||
        combined.includes('odyssey') ||
        combined.includes('sienna') ||
        combined.includes('pacifica') ||
        combined.includes('voyager') ||
        combined.includes('transit') ||
        combined.includes('promaster') ||
        combined.includes('sprinter') ||
        combined.includes('metris') ||
        combined.includes('express') ||
        combined.includes('savana') ||
        combined.includes('nv') || // Nissan NV
        combined.includes('econoline') ||
        combined.includes('e-series')
    ) {
        return 'Van';
    }

    // ========== SUV / CROSSOVER ==========
    if (
        combined.includes('suv') ||
        combined.includes('sport utility') ||
        combined.includes('crossover') ||
        combined.includes('utility vehicle') ||
        combined.includes('sport-utility') ||
        // Specific SUV models
        combined.includes('cx-') || // Mazda CX-series
        combined.includes('rav4') ||
        combined.includes('cr-v') ||
        combined.includes('highlander') ||
        combined.includes('pilot') ||
        combined.includes('explorer') ||
        combined.includes('pathfinder') ||
        combined.includes('rogue') ||
        combined.includes('equinox') ||
        combined.includes('traverse') ||
        combined.includes('tahoe') ||
        combined.includes('suburban') ||
        combined.includes('yukon') ||
        combined.includes('escalade') ||
        combined.includes('durango') ||
        combined.includes('4runner') ||
        combined.includes('sequoia') ||
        combined.includes('armada') ||
        combined.includes('expedition') ||
        combined.includes('navigator') ||
        combined.includes('grand cherokee') ||
        combined.includes('cherokee') ||
        combined.includes('wrangler') ||
        combined.includes('compass') ||
        combined.includes('patriot') ||
        combined.includes('passport') ||
        combined.includes('santa fe') ||
        combined.includes('tucson') ||
        combined.includes('kona') ||
        combined.includes('venue') ||
        combined.includes('palisade') ||
        combined.includes('telluride') ||
        combined.includes('sorento') ||
        combined.includes('sportage') ||
        combined.includes('soul') ||
        combined.includes('seltos') ||
        combined.includes('edge') ||
        combined.includes('escape') ||
        combined.includes('bronco') ||
        combined.includes('ecosport') ||
        combined.includes('flex') ||
        combined.includes('terrain') ||
        combined.includes('acadia') ||
        combined.includes('envision') ||
        combined.includes('encore') ||
        combined.includes('enclave') ||
        combined.includes('blazer') ||
        combined.includes('trailblazer') ||
        combined.includes('trax') ||
        combined.includes('xt4') ||
        combined.includes('xt5') ||
        combined.includes('xt6') ||
        combined.includes('qx') || // Infiniti QX series
        combined.includes('glc') || // Mercedes GLC
        combined.includes('gle') || // Mercedes GLE
        combined.includes('gla') || // Mercedes GLA
        combined.includes('glb') || // Mercedes GLB
        combined.includes('gls') || // Mercedes GLS
        combined.includes('q3') || // Audi Q3
        combined.includes('q5') || // Audi Q5
        combined.includes('q7') || // Audi Q7
        combined.includes('q8') || // Audi Q8
        combined.includes('x1') || // BMW X1
        combined.includes('x2') || // BMW X2
        combined.includes('x3') || // BMW X3
        combined.includes('x4') || // BMW X4
        combined.includes('x5') || // BMW X5
        combined.includes('x6') || // BMW X6
        combined.includes('x7') || // BMW X7
        combined.includes('macan') || // Porsche
        combined.includes('cayenne') || // Porsche
        combined.includes('tiguan') ||
        combined.includes('atlas') ||
        combined.includes('taos') ||
        combined.includes('forester') ||
        combined.includes('ascent') ||
        combined.includes('crosstrek')
    ) {
        return 'SUV';
    }

    // ========== WAGON ==========
    if (
        combined.includes('wagon') ||
        combined.includes('estate') ||
        combined.includes('sportswagon') ||
        (combined.includes('touring') && (combined.includes('volkswagen') || combined.includes('vw'))) ||
        // Specific wagon models
        combined.includes('outback') ||
        combined.includes('impreza wagon') ||
        combined.includes('v60') ||
        combined.includes('v90') ||
        combined.includes('e-class wagon') ||
        combined.includes('alltrack')
    ) {
        return 'Wagon';
    }

    // ========== CONVERTIBLE ==========
    if (
        combined.includes('convertible') ||
        combined.includes('cabriolet') ||
        combined.includes('roadster') ||
        combined.includes('spyder') ||
        combined.includes('drop top') ||
        // Specific convertible models
        combined.includes('miata') ||
        combined.includes('mx-5') ||
        combined.includes('mustang convertible') ||
        combined.includes('camaro convertible') ||
        combined.includes('corvette convertible') ||
        combined.includes('boxster') ||
        (combined.includes('911') && combined.includes('cabriolet')) ||
        combined.includes('z4')
    ) {
        return 'Convertible';
    }

    // ========== COUPE ==========
    if (
        combined.includes('coupe') ||
        (combined.includes('2 door') && !combined.includes('hatch') && !combined.includes('convertible')) ||
        (combined.includes('2-door') && !combined.includes('hatch') && !combined.includes('convertible')) ||
        (combined.includes('2dr') && !combined.includes('hatch') && !combined.includes('convertible')) ||
        // Specific coupe models
        (combined.includes('mustang') && !combined.includes('convertible')) ||
        (combined.includes('camaro') && !combined.includes('convertible')) ||
        (combined.includes('challenger') && !combined.includes('convertible')) ||
        (combined.includes('corvette') && !combined.includes('convertible')) ||
        combined.includes('gt-r') ||
        combined.includes('370z') ||
        combined.includes('350z') ||
        combined.includes('supra') ||
        combined.includes('86') ||
        combined.includes('brz') ||
        combined.includes('genesis coupe') ||
        combined.includes('civic coupe') ||
        combined.includes('accord coupe')
    ) {
        return 'Coupe';
    }

    // ========== HATCHBACK ==========
    if (
        combined.includes('hatchback') ||
        combined.includes('hatch') ||
        combined.includes('5 door') ||
        combined.includes('5-door') ||
        combined.includes('5dr') ||
        // Specific hatchback models
        combined.includes('civic hatchback') ||
        combined.includes('golf') ||
        combined.includes('jetta gli') ||
        combined.includes('mazda3 hatchback') ||
        combined.includes('corolla hatchback') ||
        combined.includes('impreza hatchback') ||
        combined.includes('fit') ||
        combined.includes('yaris') ||
        combined.includes('versa note') ||
        combined.includes('veloster') ||
        combined.includes('elantra gt') ||
        combined.includes('focus hatch') ||
        combined.includes('fiesta') ||
        combined.includes('cruze hatch') ||
        combined.includes('spark') ||
        combined.includes('sonic') ||
        combined.includes('rio') ||
        combined.includes('forte5') ||
        combined.includes('mazda2') ||
        (combined.includes('cooper') && !combined.includes('countryman')) // Mini Cooper
    ) {
        return 'Hatchback';
    }

    // ========== SEDAN (Default fallback) ==========
    // If we couldn't determine it's any other type, default to sedan
    return 'Sedan';
};
